// OnboardingStep3.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { submitAccessibilityAtom } from "@/store/atoms";
import Switch from "@/components/common/Switch";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useI18N, useZoomDetection, useModalHeight } from "common/hooks";
import { createPageAnalytics } from "common/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import WideButton from "@/components/common/WideButton";
import { ArrowLeft } from "lucide-react";
import { StandardButton } from "@/components/common/StandardButton";

interface OnboardingStep3Props {
  title: string;
  subtitle: string;
}

const OnboardingStep3 = ({ title, subtitle }: OnboardingStep3Props) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const isDark = useDarkMode();
  const { modalStyle, modalClassName } = useModalHeight();
  //set index for swipable views
  const { setScreenIndex } = useAuthModals();
  const profileSetupAnalytics = createPageAnalytics("profile_setup");

  //get and store the data for entire onBoarding steps
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);

  useEffect(() => {
    profileSetupAnalytics.track("page_view_page", {
      navigation_direction: "next",
      previous_step_index: 2,
      step_index: 3,
      step_name: "profile_visibility",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBack = async () => {
    setScreenIndex(AuthModalScreen.OnboardingStep2);
  };

  const onContinue = async () => {
    //move to next step
    setScreenIndex(AuthModalScreen.OnboardingStep4);
  };

  const handleToggleSwitch = () => {
    setSubmitData((prev) => ({
      ...prev,
      makeDisabilityVisibleValue: !(
        submitData?.makeDisabilityVisibleValue || false
      ),
    }));
  };

  console.log("submitData from OnboardingStep2", submitData);

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div
      style={modalStyle}
      className="flex flex-col rounded-xl bg-bg-brand-secondary-default overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={onBack}
          variant="back"
          icon={<ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />}
          aria-label="Go back"
          tabIndex={0}
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {i18n.get("profileSetup")}
        </h1>
      </header>
      <div className="px-8 md:px-16 flex-1 overflow-y-auto">
        <div>
          <h2 className="pt-6 text-fg-neutral-strong title-m-bold">{title}</h2>
          <p className="title-s-semibold text-fg-neutral-strong mt-4">
            {subtitle}
          </p>
        </div>
        <div>
          <hr className="w-full border-border-token-brand-minimal my-2" />
        </div>
        <div aria-label="Model Content">
          {/* content comes here */}
          <div>
            <div className="mb-4">
              <p className="title-s-semibold md:title-s-semibold text-fg-neutral-strong mb-3">
                {i18n.get(
                  "doYouWantToMakeYourDisabilityVisibleToTheSociabilityCommunity",
                )}
              </p>
              <p className="body-l-semibold !text-fg-brand-subtle block">
                {i18n.get("helpOtherUsersBetterUnderstandYourReviews")}
              </p>
            </div>
            <div className="flex justify-center h-[180px] items-center">
              <img
                src="/images/disabilityVisible.svg"
                alt={i18n.get("helpOtherUsersBetterUnderstandYourReviews")}
                className="h-[180px]"
              />
            </div>
            <div className="flex justify-between gap-4 items-center pt-6 mb-8">
              <div>
                <p className="title-s-semibold md:title-s-semibold mb-1 text-fg-neutral-strong">
                  {i18n.get("makeYourDisabilityPublic")}
                </p>
                <p className="label-m-medium text-fg-neutral-strong">
                  {i18n.get("helpOtherUsersBetterUnderstandYourReviews")}
                </p>
              </div>
              <div tabIndex={0}>
                <Switch
                  isOn={submitData?.makeDisabilityVisibleValue || false}
                  onChange={() => handleToggleSwitch()}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-8 items-center justify-center flex flex-col pt-2">
          <WideButton type="filled" onClick={() => onContinue()}>
            {i18n.get("continue")}
          </WideButton>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep3;
